import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';

function contactLabel(row) {
  const name = String(row.displayName ?? '').trim();
  const email = String(row.email ?? '').trim();
  if (name && email) return `${name} · ${email}`;
  return name || email || 'Contact';
}

export default function HubupFormAnnuaireRecipientModal({
  open,
  contacts,
  selectedIds,
  onClose,
  onConfirm,
}) {
  const [query, setQuery] = useState('');
  const [draftSelected, setDraftSelected] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDraftSelected(Array.isArray(selectedIds) ? [...selectedIds] : []);
    }
  }, [open, selectedIds]);

  const filtered = useMemo(() => {
    const list = Array.isArray(contacts) ? contacts : [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const email = String(row.email ?? '').toLowerCase();
      const name = String(row.displayName ?? '').toLowerCase();
      const id = String(row.hubupUserId ?? '').toLowerCase();
      return email.includes(q) || name.includes(q) || id.includes(q);
    });
  }, [contacts, query]);

  const selectedSet = useMemo(() => new Set(draftSelected), [draftSelected]);

  const toggleId = (hubupUserId) => {
    const id = String(hubupUserId ?? '').trim();
    if (!id) return;
    setDraftSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllVisible = () => {
    const ids = filtered
      .map((r) => String(r.hubupUserId ?? '').trim())
      .filter(Boolean);
    setDraftSelected((prev) => {
      const set = new Set(prev);
      for (const id of ids) set.add(id);
      return [...set];
    });
  };

  const clearAll = () => setDraftSelected([]);

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hubup-recipient-modal-title"
    >
      <div className="relative w-full max-w-4xl max-h-[88vh] rounded-xl border border-border bg-card shadow-elevation-3 flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <h2
              id="hubup-recipient-modal-title"
              className="text-lg font-semibold text-foreground"
            >
              Choisir les destinataires
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Personnes repérées dans vos formulaires. Seules elles verront l’annonce
              et recevront la notification push.
            </p>
          </div>
          <button
            type="button"
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
            aria-label="Fermer"
            onClick={onClose}
          >
            <Icon name="X" className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border flex flex-col sm:flex-row gap-3 sm:items-center shrink-0">
          <label className="flex-1 relative">
            <span className="sr-only">Rechercher</span>
            <Icon
              name="Search"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nom, e-mail…"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-input bg-background text-sm"
              autoFocus
            />
          </label>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={selectAllVisible}>
              Tout sélectionner
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={clearAll}>
              Tout effacer
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-[12rem]">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Aucun contact ne correspond à votre recherche.
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((row) => {
                const id = String(row.hubupUserId ?? '').trim();
                if (!id) return null;
                const checked = selectedSet.has(id);
                return (
                  <li key={row.id || id}>
                    <label
                      className={`flex items-start gap-3 rounded-lg border px-3 py-3 cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary/50 bg-primary/5'
                          : 'border-border hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleId(id)}
                        className="mt-1 rounded border-input shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground truncate">
                          {contactLabel(row)}
                        </span>
                        {row.formName ? (
                          <span className="block text-xs text-muted-foreground mt-0.5 truncate">
                            Formulaire : {row.formName}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
          <p className="text-sm text-muted-foreground tabular-nums">
            {draftSelected.length} personne{draftSelected.length !== 1 ? 's' : ''}{' '}
            sélectionnée{draftSelected.length !== 1 ? 's' : ''}
          </p>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => onConfirm?.(draftSelected)}
              disabled={draftSelected.length === 0}
            >
              Valider
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
